import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import { 
  Button, 
  Container, 
  Typography, 
  Box, 
  Paper, 
  Avatar, 
  Divider,
  CardContent,
  Chip
} from "@mui/material";
import { 
  Login as LoginIcon, 
  Logout as LogoutIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Home as HomeIcon
} from "@mui/icons-material";

export default function Login() {
  const { loginWithRedirect, isAuthenticated, logout, user } = useAuth0();
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={24}
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
          }}
        >
          <Box
            sx={{
              background: 'linear-gradient(135deg, #00AEEF 0%, #0078d4 100%)',
              color: 'white',
              padding: 4,
              textAlign: 'center'
            }}
          >
            <Avatar
              sx={{
                width: 80,
                height: 80,
                margin: '0 auto 16px',
                background: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)',
              }}
            >
              <BusinessIcon sx={{ fontSize: 40 }} />
            </Avatar>
            <Typography variant="h4" component="h1" fontWeight="bold">
              Cryo Facturación
            </Typography>
            <Typography variant="subtitle1" sx={{ opacity: 0.9, mt: 1 }}>
              Sistema de Gestión de Facturación
            </Typography>
          </Box>

          <CardContent sx={{ padding: 4 }}>
            {isAuthenticated ? (
              <Box>
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Avatar
                    sx={{
                      width: 60,
                      height: 60,
                      margin: '0 auto 16px',
                      background: 'linear-gradient(135deg, #00AEEF, #0078d4)',
                    }}
                  >
                    <PersonIcon sx={{ fontSize: 30 }} />
                  </Avatar>
                  <Typography variant="h5" gutterBottom>
                    ¡Bienvenido!
                  </Typography>
                  <Typography variant="h6" color="primary" gutterBottom>
                    {user.name}
                  </Typography>
                  <Chip 
                    label="Sesión Activa" 
                    color="success" 
                    size="small"
                    sx={{ mt: 1 }}
                  />
                </Box>

                <Divider sx={{ my: 3 }} />

                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  fullWidth
                  startIcon={<HomeIcon />}
                  onClick={() => navigate('/home')}
                  sx={{
                    py: 1.5,
                    mb: 2,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                    background: 'linear-gradient(135deg, #00AEEF 0%, #0078d4 100%)',
                    boxShadow: '0 4px 14px 0 rgba(0, 174, 239, 0.3)',
                    '&:hover': {
                      boxShadow: '0 6px 20px 0 rgba(0, 174, 239, 0.4)',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  Ir al Home
                </Button>

                <Button
                  variant="contained"
                  color="error"
                  size="large"
                  fullWidth
                  startIcon={<LogoutIcon />}
                  onClick={() => logout({ returnTo: window.location.origin })}
                  sx={{
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                    boxShadow: '0 4px 14px 0 rgba(244, 67, 54, 0.3)',
                    '&:hover': {
                      boxShadow: '0 6px 20px 0 rgba(244, 67, 54, 0.4)',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  Cerrar Sesión
                </Button>
              </Box>
            ) : (
              <Box>
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                  <Typography variant="h5" gutterBottom color="text.primary">
                    Acceder al Sistema
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Inicia sesión con tu cuenta para acceder a todas las funcionalidades
                  </Typography>
                </Box>

                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  startIcon={<LoginIcon />}
                  onClick={() => loginWithRedirect()}
                  sx={{
                    py: 2,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                    background: 'linear-gradient(135deg, #00AEEF 0%, #0078d4 100%)',
                    boxShadow: '0 4px 14px 0 rgba(0, 174, 239, 0.3)',
                    '&:hover': {
                      boxShadow: '0 6px 20px 0 rgba(0, 174, 239, 0.4)',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  Iniciar Sesión con Auth0
                </Button>

                <Box sx={{ mt: 3, textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    Al iniciar sesión, aceptas nuestros términos de servicio
                  </Typography>
                </Box>
              </Box>
            )}
          </CardContent>
        </Paper>
      </Container>
    </Box>
  );
}
